<template>
  <section class="c2c-meta">
    <div class="c2c-meta-header">
      <div class="c2c-meta-title">
        <span class="c2c-meta-icon" aria-hidden="true">
          <Play :size="20" />
        </span>
        <strong class="c2c-meta-name" :title="file.name">{{ file.name }}</strong>
      </div>
      <span class="c2c-meta-size">{{ formatBytes(file.size) }}</span>
    </div>

    <div class="c2c-meta-chips">
      <span class="c2c-chip">{{ meta.width }}×{{ meta.height }}</span>
      <span class="c2c-chip" :class="{ 'c2c-chip--warn': !meta.duration || meta.duration < 1 }">
        {{ meta.duration && meta.duration >= 1 ? formatDuration(meta.duration) : t('metadata.duration_unknown') }}
      </span>
      <span class="c2c-chip">{{ meta.fps ? meta.fps + ' fps' : '?' }}</span>
      <span class="c2c-chip c2c-chip--code">{{ meta.containerFormat || '?' }}</span>
      <span class="c2c-chip c2c-chip--code">{{ meta.videoCodec || 'no video' }}</span>
      <span v-if="meta.audioCodec" class="c2c-chip c2c-chip--code">{{ meta.audioCodec }}</span>
      <span v-else class="c2c-chip c2c-chip--muted">no audio</span>
    </div>

    <!-- Status notices -->
    <div v-if="passthrough" class="c2c-meta-notice c2c-meta-notice--info">
      <Lightbulb :size="16" />
      <span>{{ t('metadata.passthrough_notice') }}</span>
    </div>
    <div v-else-if="isHEVC" class="c2c-meta-notice c2c-meta-notice--info">
      <Info :size="16" />
      <span>{{ t('metadata.hevc_notice') }}</span>
    </div>
    <div v-if="isLong" class="c2c-meta-notice c2c-meta-notice--warning">
      <AlertTriangle :size="16" />
      <span>{{ t('metadata.long_notice', { duration: formatDuration(meta.duration) }) }}</span>
    </div>
    <div v-if="isOverDurationCap" class="c2c-meta-notice c2c-meta-notice--error">
      <CircleAlert :size="16" />
      <span>{{ t('metadata.over_cap_notice', { limit: formatDuration(MAX_DURATION_SECONDS) }) }}</span>
    </div>
    <div v-if="isLargeFile" class="c2c-meta-notice c2c-meta-notice--warning">
      <AlertTriangle :size="16" />
      <span>{{ t('metadata.large_notice', { size: formatBytes(file.size) }) }}</span>
    </div>
    <div v-if="isAudioOnly" class="c2c-meta-notice c2c-meta-notice--error">
      <CircleAlert :size="16" />
      <span>{{ t('metadata.audio_only_notice') }}</span>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { AlertTriangle, CircleAlert, Info, Lightbulb, Play } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import type { VideoMeta } from "@/lib/types"
import { MAX_DURATION_SECONDS } from "@/lib/config"

const { t } = useI18n()
const props = defineProps<{ file: File; meta: VideoMeta; passthrough: boolean }>()

const isHEVC = computed(() =>
  props.meta.videoCodec.startsWith("hvc") || props.meta.videoCodec.startsWith("hev") ||
  props.meta.videoCodec === "hevc" || props.meta.videoCodec === "h265")
// "Long but still allowed" advisory: 30+ minutes, up to the hard cap.
const isLong = computed(() => props.meta.duration > 30 * 60 && props.meta.duration <= MAX_DURATION_SECONDS)
const isOverDurationCap = computed(() => props.meta.duration > MAX_DURATION_SECONDS)
const isLargeFile = computed(() => props.file.size > 100 * 1024 * 1024)
const isAudioOnly = computed(() => props.meta.width === 0 || props.meta.height === 0)

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "0 B"
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + " MB"
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB"
}

function formatDuration(sec: number): string {
  if (!sec || sec <= 0) return "0s"
  const m = Math.floor(sec / 60)
  const s = Math.round(sec % 60)
  if (m === 0) return s + "s"
  const h = Math.floor(m / 60)
  if (h === 0) return m + "m " + s + "s"
  return h + "h " + (m % 60) + "m"
}
</script>

<style scoped>
.c2c-meta {
  background: #f8f9fa;
  border: 1px solid #c8ccd1;
  border-radius: 8px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.c2c-meta-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.c2c-meta-title {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.c2c-meta-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: #54595d;
}
.c2c-meta-icon :deep(svg) { width: 16px; height: 16px; }
.c2c-meta-name {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #202122;
}
.c2c-meta-size {
  flex-shrink: 0;
  font-size: 12px;
  color: #54595d;
  font-family: ui-monospace, monospace;
}

/* Chips row: one line, scrolled rather than wrapped, so the file facts read
   as a single strip instead of a ragged block. */
.c2c-meta-chips {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 4px;
  overflow-x: auto;
  scrollbar-width: thin;
  padding-bottom: 2px;
}
.c2c-chip {
  font-size: 12px;
  line-height: 18px;
  height: 22px;
  padding: 0 8px;
  /* Squarish. The 12px radius made these read as toy pills. */
  border-radius: 4px;
  background: #fff;
  border: 1px solid #c8ccd1;
  color: #202122;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
}
/* Same metrics as the others. These were 11px, which is why the codec chips
   sat visibly higher than the resolution and duration ones. */
.c2c-chip--code {
  font-family: ui-monospace, monospace;
  font-size: 12px;
  background: #eaecf0;
  border-color: #a2a9b1;
}
.c2c-chip--muted { color: #72777d; }
.c2c-chip--warn { border-color: #f0d795; background: #fef6e7; color: #886600; }

/* Notices */
.c2c-meta-notice {
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.5;
  display: flex;
  gap: 7px;
  align-items: flex-start;
}
.c2c-meta-notice--info { background: #eaf3ff; color: #2a4b8d; border: 1px solid #c8d6f0; }
.c2c-meta-notice--warning { background: #fef6e7; color: #886600; border: 1px solid #f0d795; }
.c2c-meta-notice--error { background: #fee7e6; color: #b32424; border: 1px solid #f0b0b0; }
.c2c-meta-notice :deep(svg) { flex-shrink: 0; margin-top: 1px; }
</style>